import { createScheduleComponent, duration } from '@play-co/components';
import { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';

/**
 * Difficulty levels for the treasure express event.
 * It selected based on the user's win rate in the last 7 days.
 */
export type Difficulty = 'easy' | 'normal' | 'hard';

/**
 * The level at which the treasure express is unlocked.
 */
export const treasureExpressUnlockLevel = 35;

/**
 * The number of tickets rewarded based on the attempt number.
 * 1st attempt will reward 3 tickets, 2nd attempt will reward 2 tickets, 3rd attempt onwards will reward 1 ticket.
 */
export const treasureExpressTicketRewards = [3, 2, 1];

/**
 * The rewards for the treasure express event.
 * Each difficulty level has its own set of rewards.
 * Rewards are divided into three stages.
 */
const easyRewards: Match2Reward[][] = [
  // 1st stage
  [
    { type: 'powerups', subType: 'bomb', duration: duration({ minutes: 30 }) },
    {
      type: 'powerups',
      subType: 'rocket',
      duration: duration({ minutes: 30 }),
    },
  ],

  // 2nd stage
  [
    { type: 'gems', amount: 100 },
    { type: 'boosters', subType: 'roulette', amount: 1 },
    { type: 'boosters', subType: 'drill', amount: 1 },
  ],

  // 3rd stage
  [
    { type: 'gems', amount: 300 },
    { type: 'boosters', subType: 'dart', amount: 2 },
    { type: 'boosters', subType: 'bullet', amount: 2 },
  ],
];

const normalRewards: Match2Reward[][] = [
  // 1st stage
  [
    { type: 'powerups', subType: 'bomb', duration: duration({ minutes: 30 }) },
    {
      type: 'powerups',
      subType: 'rocket',
      duration: duration({ minutes: 30 }),
    },
  ],

  // 2nd stage
  [
    { type: 'gems', amount: 200 },
    { type: 'boosters', subType: 'roulette', amount: 1 },
    { type: 'boosters', subType: 'drill', amount: 1 },
  ],

  // 3rd stage
  [
    { type: 'gems', amount: 400 },
    { type: 'boosters', subType: 'dart', amount: 2 },
    { type: 'boosters', subType: 'bullet', amount: 2 },
  ],
];
const hardRewards: Match2Reward[][] = [
  // 1st stage
  [
    { type: 'powerups', subType: 'bomb', duration: duration({ minutes: 30 }) },
    {
      type: 'powerups',
      subType: 'rocket',
      duration: duration({ minutes: 30 }),
    },
  ],

  // 2nd stage
  [
    { type: 'gems', amount: 300 },
    { type: 'boosters', subType: 'roulette', amount: 1 },
    { type: 'boosters', subType: 'drill', amount: 1 },
  ],

  // 3rd stage
  [
    { type: 'gems', amount: 500 },
    { type: 'boosters', subType: 'dart', amount: 2 },
    { type: 'boosters', subType: 'bullet', amount: 2 },
  ],
];
const hardRewardsEnabledBucket: Match2Reward[][] = [
  // 1st stage
  [
    { type: 'powerups', subType: 'bomb', duration: duration({ minutes: 60 }) },
    {
      type: 'powerups',
      subType: 'rocket',
      duration: duration({ minutes: 60 }),
    },
  ],

  // 2nd stage
  [
    { type: 'gems', amount: 200 },
    { type: 'boosters', subType: 'roulette', amount: 1 },
    { type: 'boosters', subType: 'drill', amount: 1 },
  ],

  // 3rd stage
  [
    { type: 'gems', amount: 400 },
    { type: 'boosters', subType: 'dart', amount: 2 },
    { type: 'boosters', subType: 'bullet', amount: 2 },
  ],
];

/**
 * A mapping of difficulty levels to milestone rewards for the Treasure Express game mode.
 * Each difficulty level is associated with a two-dimensional array of rewards, where each
 * inner array represents a set of rewards for a specific milestone.
 *
 * @typeParam Difficulty - Represents the difficulty levels (e.g., 'easy', 'normal', 'hard').
 * @typeParam Match2Reward - Represents the structure of a reward for a milestone.
 *
 * @property easy - Rewards for the 'easy' difficulty level.
 * @property normal - Rewards for the 'normal' difficulty level.
 * @property hard - Rewards for the 'hard' difficulty level.
 */
export const treasureExpressMilestoneRewards: Record<
  Difficulty,
  Match2Reward[][]
> = {
  easy: easyRewards,
  normal: normalRewards,
  hard: hardRewards,
};
/**
 * The number of tickets required to claim each milestone.
 */
export const treasureExpressMilestones: Record<
  Difficulty,
  [number, number, number]
> = {
  easy: [10, 30, 60],
  normal: [20, 50, 90],
  hard: [30, 70, 120],
};

/**
 * Schedule for the treasure express.
 */
export const treasureExpressSchedule = createScheduleComponent(
  '2024-03-17',
  'Asia/Tokyo',
  {
    weekly: [{ day: 'Thursday', time: '23:00', duration: { days: 3 } }],
  },
);

/**
 * Duration of the treasure express event.
 */
export const treasureExpressEventDuration = duration({ days: 3 });

/**
 * Expiration time for old treasure express entries.
 */
export const treasureExpressExpirationTime = duration({ hours: 48 });
